/**
 * Búsqueda Hunting — stepper de punta a punta (PLAN_HUNTING_APOLLO.md §5.1, MVP §9):
 *   1 Buscar (gratis)  → 2 Seleccionar (lista parcial)  → 3 Datos de contacto
 *   (enriquecer: cobra puntos por email y teléfono obtenidos)  → 4 Invitar a una
 *   vacante ATS (correo del portal)  → 5 Resumen.
 *
 * Apollo solo entrega nombre de pila, apellido censurado, cargo y empresa en la
 * búsqueda; email, LinkedIn y ubicación se obtienen al enriquecer (con costo),
 * por eso el costo se muestra ANTES de pedirlos.
 *
 * Teléfono (2026-10-21): opcional. Apollo lo envía después al webhook del
 * backend; el paso 3 lo muestra apenas llega. Todo lo obtenido queda guardado
 * en el backend (huntingProfiles) y no se vuelve a cobrar: en la lista aparece
 * como "Ya obtenido".
 * Puntos (2026-10-21): cada email 1/17 de punto y cada teléfono 1/2. Al usuario
 * se le muestran solo puntos, nunca créditos de Apollo.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Stepper, Step, StepLabel, TextField, Button,
  Autocomplete, Chip, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Alert, CircularProgress, Link, Tooltip,
  Accordion, AccordionSummary, AccordionDetails, Grid, Divider, OutlinedInput, ListSubheader,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  CheckCircle as YesIcon,
  Cancel as NoIcon,
  OpenInNew as OpenInNewIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../config/api';
import useStore from '../config/store';
import { getStatusLabel } from '../ats/positions/positionStatus';
import { formatValidationPhone } from '../ats/positions/AtsValidateDialog';
import { DEFAULT_PRICING, fmtPoints } from './huntingPricing';

const PURPLE = '#6A5AF9';
const PURPLE_DARK = '#5B4DE8';

// Puntos por parte: los manda el backend en `pricing` (el respaldo y el formato
// están en huntingPricing.js).
// Teléfono: Apollo lo envía después al webhook del backend. Mientras la
// pantalla esté abierta se consulta cada 5 s, hasta 3 minutos; se guarda igual
// si el usuario se va.
const PHONE_POLL_MS = 5000;
const PHONE_POLL_MAX = 36;

// ── Filtros de búsqueda (espejo de apollo.service.searchPeople) ─────────────
// Básicos: siempre visibles. Avanzados: acordeón. Los arrays van como chips.
const ARRAY_KEYS = [
  'titles', 'locations', 'notTitles', 'pastTitles', 'departments', 'seniorities', 'emailStatuses',
  'orgDomains', 'excludeOrgDomains', 'orgLocations', 'industries', 'orgKeywords', 'employeeRanges',
  'orgJobTitles', 'orgJobLocations', 'techAny', 'techAll', 'techNone',
];
const DEFAULT_FILTERS = {
  titles: [], keywords: '', locations: ['Chile'], level: '', verifiedOnly: true,
  includeSimilarTitles: true, personName: '', notTitles: [], pastTitles: [], departments: [],
  seniorities: [], emailStatuses: [],
  orgDomains: [], excludeOrgDomains: [], orgLocations: [], industries: [], orgKeywords: [],
  employeeRanges: [], revenueMin: '', revenueMax: '',
  headcountGrowthMonths: '', headcountGrowthMin: '', headcountGrowthMax: '',
  orgJobTitles: [], orgJobLocations: [], orgJobsMin: '', orgJobsMax: '', orgJobPostedFrom: '', orgJobPostedTo: '',
  techAny: [], techAll: [], techNone: [],
};
const BASIC_KEYS = ['titles', 'keywords', 'locations', 'level', 'verifiedOnly'];
const ADVANCED_KEYS = Object.keys(DEFAULT_FILTERS).filter((k) => !BASIC_KEYS.includes(k) && k !== 'includeSimilarTitles');

const SENIORITIES = ['owner', 'founder', 'c_suite', 'partner', 'vp', 'head', 'director', 'manager', 'senior', 'entry', 'intern'];
const EMAIL_STATUSES = ['verified', 'likely to engage', 'unverified', 'unavailable'];
const EMPLOYEE_RANGES = ['1,10', '11,20', '21,50', '51,100', '101,200', '201,500', '501,1000', '1001,2000', '2001,5000', '5001,10000', '10001,1000000'];
const GROWTH_WINDOWS = [6, 12, 24];
// Departamentos de Apollo (person_department_or_subdepartments[]); freeSolo por si hay otros
const DEPARTMENTS = [
  'c_suite', 'executive', 'product_management', 'master_engineering_technical', 'master_information_technology',
  'master_finance', 'master_human_resources', 'master_legal', 'master_marketing', 'master_medical_health',
  'master_operations', 'master_sales', 'consulting', 'education',
];
// Industrias (nombres LinkedIn en minúsculas, como los devuelve Apollo en organization.industry); freeSolo
const INDUSTRIES = [
  'information technology & services', 'computer software', 'internet', 'telecommunications',
  'financial services', 'banking', 'insurance', 'accounting', 'management consulting',
  'staffing & recruiting', 'human resources', 'outsourcing/offshoring', 'marketing & advertising',
  'retail', 'consumer goods', 'wholesale', 'food & beverages', 'food production', 'farming', 'fishery',
  'mining & metals', 'oil & energy', 'utilities', 'renewables & environment', 'chemicals',
  'construction', 'real estate', 'architecture & planning', 'civil engineering',
  'logistics & supply chain', 'transportation/trucking/railroad', 'airlines/aviation', 'maritime',
  'automotive', 'machinery', 'electrical/electronic manufacturing', 'industrial automation',
  'hospital & health care', 'pharmaceuticals', 'medical devices',
  'education management', 'higher education', 'e-learning',
  'government administration', 'nonprofit organization management', 'legal services',
  'media production', 'entertainment', 'hospitality', 'leisure, travel & tourism',
];
const employeeRangeLabel = (r) => {
  const [min, max] = r.split(',');
  return Number(max) >= 1000000 ? `${Number(min).toLocaleString()}+` : `${Number(min).toLocaleString()}–${Number(max).toLocaleString()}`;
};

// Nombre en la lista (2026-10-22): el nombre de pila completo y del resto solo
// la inicial y asteriscos. Con el nombre completo (persona ya obtenida) va un *
// por letra: "Bastian S*** P****". Sin él, Apollo entrega solo su apellido
// oculto ("Pa***o", y a veces solo una parte del apellido): se muestra la
// inicial y *** ("Bastian P***")
const maskWord = (w) => (w.length <= 1 ? w : `${w.charAt(0)}${'*'.repeat(w.length - 1)}`);
const maskedFullName = (fullName, firstName) => {
  const words = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  const given = String(firstName || '').trim().split(/\s+/).filter(Boolean);
  // Se deja completo el nombre de pila de Apollo si el nombre completo empieza con él
  const startsWithGiven = given.length > 0
    && given.every((g, i) => (words[i] || '').toLowerCase() === g.toLowerCase());
  const keep = startsWithGiven ? given.length : 1;
  return [...words.slice(0, keep), ...words.slice(keep).map(maskWord)].join(' ');
};
const listName = (p) => {
  if (p.obtained?.name) return maskedFullName(p.obtained.name, p.firstName);
  const initial = String(p.lastNameObfuscated || '').replace(/[^\p{L}\p{N}]/gu, '').charAt(0);
  return [p.firstName, initial ? `${initial}***` : null].filter(Boolean).join(' ');
};

const responseData = (res) => (res?.data?.response?.status_response === 'OK' ? res.data.response.data : null);
const responseError = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.response?.message || err?.message || fallback;
// Hunting bloqueado por organización, puntos o plan: 403 del backend con `code` (2026-10-21)
const huntingBlockError = (err, t) => {
  const payload = err?.response?.data?.response;
  switch (payload?.code) {
    case 'ORGANIZATION_FORBIDDEN': return t('hunting.search.errors.organizationForbidden');
    case 'NO_POINTS_SYSTEM': return t('hunting.search.errors.noPointsSystem');
    case 'SERVICE_NOT_AVAILABLE': return t('hunting.search.errors.serviceNotAvailable');
    case 'INSUFFICIENT_POINTS':
      return t('hunting.search.errors.insufficientPoints', {
        required: fmtPoints(payload.required),
        remaining: fmtPoints(Math.max(0, Number(payload.remaining) || 0)),
      });
    default: return null;
  }
};

const Flag = ({ value, title }) => (
  <Tooltip title={title}>
    {value
      ? <YesIcon fontSize="small" sx={{ color: '#10B981' }} />
      : <NoIcon fontSize="small" sx={{ color: '#D1D5DB' }} />}
  </Tooltip>
);

const EmailStatusChip = ({ status, reliable }) => {
  const { t } = useTranslation();
  if (!status) return <Chip size="small" label={t('hunting.emailStatus.unavailable')} />;
  return (
    <Chip
      size="small"
      label={t(`hunting.emailStatus.${status.replace(/\s+/g, '_')}`, { defaultValue: status })}
      sx={reliable
        ? { backgroundColor: '#D1FAE5', color: '#065F46' }
        : { backgroundColor: '#FEF3C7', color: '#92400E' }}
    />
  );
};

/**
 * Teléfono de una persona enriquecida (llega después por el webhook de Apollo).
 * `late`: la pantalla dejó de consultar y todavía no llega (2026-10-21).
 */
const PhoneCell = ({ phone, late = false }) => {
  const { t } = useTranslation();
  const status = phone?.status || 'not_requested';
  if (status === 'pending' && late) {
    return (
      <Tooltip title={t('hunting.search.enriched.phoneLateTip')}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{t('hunting.search.enriched.phoneLate')}</Typography>
      </Tooltip>
    );
  }
  if (status === 'pending') {
    return (
      <Tooltip title={t('hunting.search.enriched.phonePendingTip')}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <CircularProgress size={14} sx={{ color: PURPLE }} />
          <Typography variant="caption" color="text.secondary">{t('hunting.search.enriched.phonePending')}</Typography>
        </Box>
      </Tooltip>
    );
  }
  if (status === 'found' && phone.primary) {
    return <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatValidationPhone(phone.primary)}</Typography>;
  }
  if (status === 'found' && phone.fallback) {
    return (
      <Tooltip title={t('hunting.search.enriched.phoneFallbackTip')}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{phone.fallback}</Typography>
      </Tooltip>
    );
  }
  if (status === 'found' || status === 'not_found') {
    return <Typography variant="caption" color="text.secondary">{t('hunting.search.enriched.phoneNotFound')}</Typography>;
  }
  return (
    <Tooltip title={t('hunting.search.enriched.phoneNotRequested')}>
      <Typography variant="caption" color="text.secondary">—</Typography>
    </Tooltip>
  );
};

/** Campo de chips (freeSolo). `options` opcional para sugerencias. */
const ChipsField = ({ label, helper, options = [], size = 'medium', value, inputValue, onInputChange, onChange }) => (
  <Autocomplete
    multiple freeSolo autoSelect size={size} options={options} value={value}
    inputValue={inputValue} onInputChange={(_, v) => onInputChange(v)}
    onChange={(_, v) => onChange(v.map((x) => String(x).trim()).filter(Boolean))}
    renderTags={(val, getTagProps) => val.map((opt, i) => (
      <Chip label={opt} size="small" {...getTagProps({ index: i })} key={`${label}-${opt}`} />
    ))}
    renderInput={(params) => <TextField {...params} label={label} helperText={helper} />}
  />
);

/** Multi-select de valores fijos con etiqueta por valor. */
const MultiSelect = ({ label, values, labelFor, value, onChange }) => (
  <FormControl fullWidth size="small">
    <InputLabel>{label}</InputLabel>
    <Select
      multiple value={value} input={<OutlinedInput label={label} />}
      onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
      renderValue={(sel) => sel.map(labelFor).join(', ')}
    >
      {values.map((v) => (
        <MenuItem key={v} value={v}>
          <Checkbox size="small" checked={value.includes(v)} sx={{ py: 0 }} />
          {labelFor(v)}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const NumberField = ({ label, helper, value, onChange }) => (
  <TextField
    fullWidth size="small" type="number" label={label} helperText={helper} value={value}
    onChange={(e) => onChange(e.target.value)} inputProps={{ min: 0 }}
  />
);

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, mb: 1, color: PURPLE }}>{children}</Typography>
);

const HuntingSearchLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { project, organization, showAlert } = useStore.getState();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Paso 1 — filtros (un solo objeto) + texto pendiente de cada campo de chips
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pending, setPending] = useState({}); // { [key]: texto escrito sin confirmar con Enter }
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const setF = (key) => (value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const setPendingFor = (key) => (value) => setPending((prev) => ({ ...prev, [key]: value }));

  // Paso 2 — resultados parciales y selección
  const [search, setSearch] = useState(null); // { total, returned, truncated, people, phoneAvailable }
  const [selected, setSelected] = useState(new Set());
  const [includePhone, setIncludePhone] = useState(false); // opcional, 1/2 punto por cada teléfono que llegue
  const [onlyWithPhone, setOnlyWithPhone] = useState(false); // ver solo quienes tienen teléfono según Apollo

  // Paso 3 — enriquecidos y selección final
  const [enrich, setEnrich] = useState(null); // { people, notFound, pointsCharged, pricing }
  const [toInvite, setToInvite] = useState(new Set());
  const [phoneWaitOver, setPhoneWaitOver] = useState(''); // teléfonos pendientes que se dejaron de consultar

  // Paso 4 — destino: { ats: [], evaluations: [] }, cargo opcional, correo opcional (default off)
  const [positions, setPositions] = useState({ ats: [], evaluations: [] });
  const [associate, setAssociate] = useState(false);
  const [positionId, setPositionId] = useState('');
  const [sendEmail, setSendEmail] = useState(false);

  // Paso 5 — resumen
  const [summary, setSummary] = useState(null); // { results, summary }

  const steps = [1, 2, 3, 4, 5].map((n) => t(`hunting.search.steps.step${n}`));

  useEffect(() => {
    if (activeStep !== 3 || !project) return;
    api.get('/hunting/positions', { params: { projectId: project } })
      .then((res) => setPositions(responseData(res) || { ats: [], evaluations: [] }))
      .catch((err) => setError(responseError(err, t('hunting.search.errors.positions'))));
  }, [activeStep, project, t]);

  const invitees = useMemo(
    () => (enrich?.people || []).filter((p) => toInvite.has(p.apolloId)),
    [enrich, toInvite],
  );

  // Teléfonos pendientes: el backend los recibe del webhook de Apollo; aquí se
  // consultan para mostrarlos apenas llegan (ver PHONE_POLL_*)
  const pendingPhoneKey = useMemo(
    () => (enrich?.people || []).filter((p) => p.phone?.status === 'pending').map((p) => p.apolloId).join(','),
    [enrich],
  );
  useEffect(() => {
    if (!pendingPhoneKey || !organization) return undefined;
    let tries = 0;
    let cancelled = false;
    const timer = setInterval(async () => {
      tries += 1;
      const last = tries >= PHONE_POLL_MAX;
      if (last) clearInterval(timer);
      try {
        const res = await api.get('/hunting/profiles', { params: { organizationId: organization, apolloIds: pendingPhoneKey } });
        const profiles = responseData(res)?.profiles || {};
        const changed = Object.values(profiles).some((x) => x?.phone && x.phone.status !== 'pending');
        if (!cancelled && changed) {
          setEnrich((prev) => (prev ? {
            ...prev,
            people: prev.people.map((p) => (profiles[p.apolloId]?.phone ? { ...p, phone: profiles[p.apolloId].phone } : p)),
          } : prev));
        }
      } catch (err) {
        // silencioso: se reintenta en el próximo intervalo
      }
      // Se dejó de consultar con teléfonos aún pendientes: "Llegará más tarde"
      // (si llega uno, cambia pendingPhoneKey y se vuelve a consultar)
      if (last && !cancelled) setPhoneWaitOver(pendingPhoneKey);
    }, PHONE_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [pendingPhoneKey, organization]);
  const phoneLate = Boolean(pendingPhoneKey) && phoneWaitOver === pendingPhoneKey;

  // ── acciones ────────────────────────────────────────────────────────────
  // Un texto escrito en un Autocomplete freeSolo sin presionar Enter no es chip
  // todavía: al buscar se toma igual (evita el "indica al menos un cargo" con el
  // campo visiblemente lleno).
  const mergePending = (values, pending) => {
    const v = pending.trim();
    return v && !values.includes(v) ? [...values, v] : values;
  };

  const isEmptyValue = (v) => (Array.isArray(v) ? v.length === 0 : v === '' || v === null || v === undefined || v === false);
  const activeAdvanced = ADVANCED_KEYS.filter((k) => !isEmptyValue(filters[k]));
  const clearAdvanced = () => {
    setFilters((prev) => ({ ...prev, ...Object.fromEntries(ADVANCED_KEYS.map((k) => [k, DEFAULT_FILTERS[k]])), includeSimilarTitles: true }));
    setPending({});
  };

  const runSearch = async () => {
    // Confirmar todo lo escrito sin Enter en los campos de chips
    const merged = { ...filters };
    ARRAY_KEYS.forEach((k) => { merged[k] = mergePending(filters[k] || [], pending[k] || ''); });
    const hasCriteria = ARRAY_KEYS.some((k) => merged[k].length > 0)
      || ['keywords', 'personName', 'revenueMin', 'revenueMax', 'headcountGrowthMonths', 'orgJobsMin', 'orgJobsMax', 'orgJobPostedFrom', 'orgJobPostedTo']
        .some((k) => !isEmptyValue(merged[k]))
      || Boolean(merged.level);
    if (!hasCriteria) {
      setError(t('hunting.search.errors.noCriteria'));
      return;
    }
    setFilters(merged); setPending({});
    setLoading(true); setError(null);
    try {
      const res = await api.post('/hunting/search', {
        ...merged, level: merged.level || null, organizationId: organization || null, projectId: project || null,
      });
      const data = responseData(res);
      if (!data) throw new Error(res?.data?.response?.message || 'NOK');
      setSearch(data);
      setSelected(new Set());
      setEnrich(null);
      setActiveStep(1);
    } catch (err) {
      setError(huntingBlockError(err, t) || responseError(err, t('hunting.search.errors.search')));
    } finally {
      setLoading(false);
    }
  };

  const runEnrich = async () => {
    if (selected.size === 0) return;
    setLoading(true); setError(null);
    try {
      const res = await api.post('/hunting/enrich', {
        apolloIds: Array.from(selected),
        organizationId: organization || null,
        projectId: project || null,
        includePhone: Boolean(includePhone && search?.phoneAvailable),
        searchId: search?.searchId || null,
      });
      const data = responseData(res);
      if (!data) throw new Error(res?.data?.response?.message || 'NOK');
      setEnrich(data);
      // Lo recién obtenido queda como "Ya obtenido" si se vuelve a la lista: el
      // costo del botón ya no cuenta su email, y su teléfono solo si nunca se
      // pidió (2026-10-21)
      const obtainedAt = new Date().toISOString();
      const gotById = new Map(data.people.map((p) => [p.apolloId, p]));
      setSearch((prev) => (prev ? {
        ...prev,
        people: prev.people.map((p) => {
          const got = gotById.get(p.apolloId);
          if (!got) return p;
          const phoneStatus = got.phone?.status || 'not_requested';
          return {
            ...p,
            obtained: {
              enrichedAt: p.obtained?.enrichedAt || obtainedAt,
              name: got.name || p.obtained?.name || null,
              hasEmail: Boolean(got.email),
              phoneStatus,
              phoneRequestable: phoneStatus === 'not_requested',
            },
          };
        }),
      } : prev));
      // Preseleccionar solo los que tienen email
      setToInvite(new Set(data.people.filter((p) => p.email).map((p) => p.apolloId)));
      setActiveStep(2);
    } catch (err) {
      setError(huntingBlockError(err, t) || responseError(err, t('hunting.search.errors.enrich')));
    } finally {
      setLoading(false);
    }
  };

  const selectedPosition = useMemo(
    () => [...(positions.ats || []), ...(positions.evaluations || [])].find((p) => p.uid === positionId) || null,
    [positions, positionId],
  );
  const selectedIsAts = Boolean(selectedPosition && (positions.ats || []).some((p) => p.uid === selectedPosition.uid));
  // El correo lleva al portal de la vacante: no se puede enviar si la vacante
  // no recibe postulaciones ahí (motivo que calcula el backend, 2026-10-08)
  const emailBlock = selectedIsAts ? selectedPosition.portalClosedReason || null : null;

  const runInvite = async () => {
    if (invitees.length === 0 || (associate && !positionId)) return;
    setLoading(true); setError(null);
    try {
      const res = await api.post('/hunting/invite', {
        projectId: project,
        organizationId: organization || null,
        searchId: search?.searchId || null,
        positionId: associate ? positionId : null,
        sendEmail: associate && selectedIsAts && sendEmail && !emailBlock,
        language: useStore.getState().language || 'es',
        candidates: invitees.map((p) => ({
          apolloId: p.apolloId,
          name: p.name,
          email: p.email,
          emailStatus: p.emailStatus,
          linkedinUrl: p.linkedinUrl,
          location: p.location,
          country: p.country,
          title: p.title,
          organizationName: p.organizationName,
        })),
      });
      const data = responseData(res);
      if (!data) throw new Error(res?.data?.response?.message || 'NOK');
      setSummary(data);
      setActiveStep(4);
      showAlert(t('hunting.search.summary.toast', { saved: data.summary.saved }), data.summary.errors ? 'warning' : 'success');
    } catch (err) {
      setError(huntingBlockError(err, t) || responseError(err, t('hunting.search.errors.invite')));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setActiveStep(0); setSearch(null); setSelected(new Set()); setEnrich(null);
    setToInvite(new Set()); setPositionId(''); setAssociate(false); setSendEmail(false); setSummary(null); setError(null);
  };

  const toggle = (set, setter, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };
  const toggleAll = (set, setter, ids) => {
    setter(set.size === ids.length ? new Set() : new Set(ids));
  };

  // ── render de pasos ─────────────────────────────────────────────────────
  // Props para los componentes de formulario hoisteados (fuera del componente
  // para que no se remonten en cada render y el input no pierda el foco).
  const chipsProps = (field) => ({
    value: filters[field], inputValue: pending[field] || '',
    onInputChange: setPendingFor(field), onChange: setF(field),
  });
  const valueProps = (field) => ({ value: filters[field], onChange: setF(field) });

  const renderSearchForm = () => (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: 2, maxWidth: 900 }}>
      {/* ── Básicos ── */}
      <Box sx={{ mb: 2 }}>
        <ChipsField {...chipsProps('titles')} label={t('hunting.search.form.titles')} helper={t('hunting.search.form.titlesHelp')} />
      </Box>
      <TextField
        fullWidth label={t('hunting.search.form.keywords')} value={filters.keywords}
        onChange={(e) => setF('keywords')(e.target.value)}
        helperText={t('hunting.search.form.keywordsHelp')} sx={{ mb: 2 }}
      />
      <Box sx={{ mb: 2 }}>
        <ChipsField {...chipsProps('locations')} label={t('hunting.search.form.locations')} helper={t('hunting.search.form.locationsHelp')}
          options={['Chile', 'Santiago, Chile', 'Perú', 'Lima, Perú', 'Colombia', 'México', 'Argentina']}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <FormControl sx={{ minWidth: 240 }} disabled={filters.seniorities.length > 0}>
          <InputLabel>{t('hunting.search.form.level')}</InputLabel>
          <Select value={filters.level} label={t('hunting.search.form.level')} onChange={(e) => setF('level')(e.target.value)}>
            <MenuItem value="">{t('hunting.search.form.levelAny')}</MenuItem>
            <MenuItem value="junior">{t('hunting.search.form.levelJunior')}</MenuItem>
            <MenuItem value="mid">{t('hunting.search.form.levelMid')}</MenuItem>
            <MenuItem value="senior">{t('hunting.search.form.levelSenior')}</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          disabled={filters.emailStatuses.length > 0}
          control={<Switch checked={filters.verifiedOnly} onChange={(e) => setF('verifiedOnly')(e.target.checked)} />}
          label={t('hunting.search.form.verifiedOnly')}
        />
      </Box>

      {/* ── Avanzados ── */}
      <Accordion
        expanded={advancedOpen} onChange={(_, v) => setAdvancedOpen(v)} elevation={0} disableGutters
        sx={{ border: '1px solid #E5E7EB', borderRadius: '8px !important', mb: 3, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <Typography fontWeight={600}>{t('hunting.search.advanced.title')}</Typography>
            {activeAdvanced.length > 0 && (
              <Chip size="small" label={t('hunting.search.advanced.active', { count: activeAdvanced.length })} sx={{ backgroundColor: '#EDE9FE', color: PURPLE }} />
            )}
            <Box sx={{ flexGrow: 1 }} />
            {activeAdvanced.length > 0 && (
              <Button size="small" onClick={(e) => { e.stopPropagation(); clearAdvanced(); }} sx={{ color: '#6B7280' }}>
                {t('hunting.search.advanced.clear')}
              </Button>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Persona */}
          <SectionTitle>{t('hunting.search.advanced.person')}</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('notTitles')} label={t('hunting.search.advanced.notTitles')} helper={t('hunting.search.advanced.notTitlesHelp')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('pastTitles')} label={t('hunting.search.advanced.pastTitles')} helper={t('hunting.search.advanced.pastTitlesHelp')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label={t('hunting.search.advanced.personName')} value={filters.personName} onChange={(e) => setF('personName')(e.target.value)} helperText={t('hunting.search.advanced.personNameHelp')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('departments')} label={t('hunting.search.advanced.departments')} helper={t('hunting.search.advanced.departmentsHelp')} options={DEPARTMENTS} />
            </Grid>
            <Grid item xs={12} md={6}>
              <MultiSelect {...valueProps('seniorities')} label={t('hunting.search.advanced.seniorities')} values={SENIORITIES} labelFor={(v) => t(`hunting.seniority.${v}`, { defaultValue: v })} />
              <Typography variant="caption" color="text.secondary">{t('hunting.search.advanced.senioritiesHelp')}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MultiSelect {...valueProps('emailStatuses')} label={t('hunting.search.advanced.emailStatuses')} values={EMAIL_STATUSES} labelFor={(v) => t(`hunting.emailStatus.${v.replace(/\s+/g, '_')}`, { defaultValue: v })} />
              <Typography variant="caption" color="text.secondary">{t('hunting.search.advanced.emailStatusesHelp')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch size="small" checked={filters.includeSimilarTitles} onChange={(e) => setF('includeSimilarTitles')(e.target.checked)} />}
                label={t('hunting.search.advanced.includeSimilarTitles')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          {/* Empresa */}
          <SectionTitle>{t('hunting.search.advanced.company')}</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('industries')} label={t('hunting.search.advanced.industries')} helper={t('hunting.search.advanced.industriesHelp')} options={INDUSTRIES} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('orgKeywords')} label={t('hunting.search.advanced.orgKeywords')} helper={t('hunting.search.advanced.orgKeywordsHelp')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('orgDomains')} label={t('hunting.search.advanced.orgDomains')} helper={t('hunting.search.advanced.orgDomainsHelp')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('excludeOrgDomains')} label={t('hunting.search.advanced.excludeOrgDomains')} helper={t('hunting.search.advanced.excludeOrgDomainsHelp')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('orgLocations')} label={t('hunting.search.advanced.orgLocations')} helper={t('hunting.search.advanced.orgLocationsHelp')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <MultiSelect {...valueProps('employeeRanges')} label={t('hunting.search.advanced.employeeRanges')} values={EMPLOYEE_RANGES} labelFor={employeeRangeLabel} />
            </Grid>
            <Grid item xs={6} md={3}>
              <NumberField {...valueProps('revenueMin')} label={t('hunting.search.advanced.revenueMin')} helper="USD" />
            </Grid>
            <Grid item xs={6} md={3}>
              <NumberField {...valueProps('revenueMax')} label={t('hunting.search.advanced.revenueMax')} helper="USD" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('hunting.search.advanced.growthWindow')}</InputLabel>
                  <Select value={filters.headcountGrowthMonths} label={t('hunting.search.advanced.growthWindow')} onChange={(e) => setF('headcountGrowthMonths')(e.target.value)}>
                    <MenuItem value="">—</MenuItem>
                    {GROWTH_WINDOWS.map((m) => <MenuItem key={m} value={m}>{t('hunting.search.advanced.growthMonths', { count: m })}</MenuItem>)}
                  </Select>
                </FormControl>
                <NumberField {...valueProps('headcountGrowthMin')} label={t('hunting.search.advanced.growthMin')} helper="%" />
                <NumberField {...valueProps('headcountGrowthMax')} label={t('hunting.search.advanced.growthMax')} helper="%" />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          {/* Contratando */}
          <SectionTitle>{t('hunting.search.advanced.hiring')}</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('orgJobTitles')} label={t('hunting.search.advanced.orgJobTitles')} helper={t('hunting.search.advanced.orgJobTitlesHelp')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipsField size="small" {...chipsProps('orgJobLocations')} label={t('hunting.search.advanced.orgJobLocations')} helper={t('hunting.search.advanced.orgJobLocationsHelp')} />
            </Grid>
            <Grid item xs={6} md={3}>
              <NumberField {...valueProps('orgJobsMin')} label={t('hunting.search.advanced.orgJobsMin')} />
            </Grid>
            <Grid item xs={6} md={3}>
              <NumberField {...valueProps('orgJobsMax')} label={t('hunting.search.advanced.orgJobsMax')} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="date" label={t('hunting.search.advanced.orgJobPostedFrom')} InputLabelProps={{ shrink: true }} value={filters.orgJobPostedFrom} onChange={(e) => setF('orgJobPostedFrom')(e.target.value)} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="date" label={t('hunting.search.advanced.orgJobPostedTo')} InputLabelProps={{ shrink: true }} value={filters.orgJobPostedTo} onChange={(e) => setF('orgJobPostedTo')(e.target.value)} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          {/* Tecnología */}
          <SectionTitle>{t('hunting.search.advanced.tech')}</SectionTitle>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('hunting.search.advanced.techHelp')}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <ChipsField size="small" {...chipsProps('techAny')} label={t('hunting.search.advanced.techAny')} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ChipsField size="small" {...chipsProps('techAll')} label={t('hunting.search.advanced.techAll')} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ChipsField size="small" {...chipsProps('techNone')} label={t('hunting.search.advanced.techNone')} />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Button
        variant="contained" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
        disabled={loading} onClick={runSearch}
        sx={{ backgroundColor: PURPLE, '&:hover': { backgroundColor: PURPLE_DARK } }}
      >
        {t('hunting.search.form.submit')}
      </Button>
    </Paper>
  );

  /** Chips de resumen de los filtros aplicados (los devuelve el backend ya saneados). */
  const renderAppliedFilters = () => {
    const f = search?.filters || {};
    const chips = [];
    const add = (key, value) => { if (!isEmptyValue(value)) chips.push({ key, value: Array.isArray(value) ? value.join(', ') : String(value) }); };
    Object.keys(DEFAULT_FILTERS).forEach((k) => {
      if (k === 'includeSimilarTitles' || k === 'verifiedOnly') return;
      if (k === 'employeeRanges') add(k, (f[k] || []).map(employeeRangeLabel));
      else if (k === 'level') add(k, f[k] ? t(`hunting.search.form.level${f[k][0].toUpperCase()}${f[k].slice(1)}`) : '');
      else if (k === 'headcountGrowthMonths') add(k, f[k] ? t('hunting.search.advanced.growthMonths', { count: f[k] }) : '');
      else add(k, f[k]);
    });
    if (f.verifiedOnly && !(f.emailStatuses || []).length) chips.push({ key: 'verifiedOnly', value: t('hunting.search.form.verifiedOnlyShort') });
    if (f.includeSimilarTitles === false) chips.push({ key: 'includeSimilarTitles', value: t('hunting.search.advanced.exactTitles') });
    if (chips.length === 0) return null;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
        {chips.map((c) => (
          <Chip key={c.key} size="small" variant="outlined" label={`${t(`hunting.filterLabels.${c.key}`, { defaultValue: c.key })}: ${c.value}`} />
        ))}
      </Box>
    );
  };

  const renderPartialResults = () => {
    const people = search?.people || [];
    // "Solo con teléfono (según Apollo)" (2026-10-22): filtra los resultados ya
    // cargados, sin buscar de nuevo. Al activarlo se desmarca a quienes quedan
    // ocultos, así "seleccionar todos" y el costo calzan con lo que se ve
    const visible = onlyWithPhone ? people.filter((p) => p.hasPhone) : people;
    const ids = visible.map((p) => p.apolloId);
    const withPhoneCount = people.filter((p) => p.hasPhone).length;
    const toggleOnlyWithPhone = (checked) => {
      setOnlyWithPhone(checked);
      if (checked) {
        const keep = new Set(people.filter((p) => p.hasPhone).map((p) => p.apolloId));
        setSelected((prev) => new Set([...prev].filter((id) => keep.has(id))));
      }
    };
    // Costo máximo en puntos: cada persona nueva con email 1/17 y cada teléfono
    // pedido 1/2. Lo ya obtenido no se vuelve a pedir, salvo que ahora se pida
    // el teléfono y se pueda (`phoneRequestable` del backend); en ese caso
    // cuenta solo el teléfono.
    const pricing = search?.pricing || DEFAULT_PRICING;
    const phoneOn = Boolean(includePhone && search?.phoneAvailable);
    const willFetch = people.filter((p) => selected.has(p.apolloId)
      && (!p.obtained || (phoneOn && p.obtained.phoneRequestable)));
    const maxPoints = willFetch.filter((p) => !p.obtained).length * pricing.email
      + (phoneOn ? willFetch.length * pricing.phone : 0);
    const enrichLabel = t('hunting.search.actions.enrich', { points: fmtPoints(maxPoints) });
    // Cómo se cobra, con los valores calculados (2026-10-22)
    const chargesTip = (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{t('hunting.search.charges.title')}</Typography>
        <Typography variant="caption" component="div">• {t('hunting.search.charges.email', { points: fmtPoints(pricing.email) })}</Typography>
        <Typography variant="caption" component="div">• {t('hunting.search.charges.phone', { points: fmtPoints(pricing.phone) })}</Typography>
        <Typography variant="caption" component="div">• {t('hunting.search.charges.max')}</Typography>
      </Box>
    );
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {t('hunting.search.results.count', { returned: search.returned, total: search.total })}
            </Typography>
            {search.truncated && (
              <Typography variant="caption" color="text.secondary">{t('hunting.search.results.truncated', { returned: search.returned })}</Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('hunting.search.results.partialNote')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(0)}>{t('hunting.search.actions.back')}</Button>
            <Button
              variant="contained" disabled={selected.size === 0 || loading} onClick={runEnrich}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ backgroundColor: PURPLE, '&:hover': { backgroundColor: PURPLE_DARK } }}
            >
              {enrichLabel}
            </Button>
            <Tooltip title={chargesTip} arrow>
              <InfoIcon fontSize="small" titleAccess={t('hunting.search.charges.title')} sx={{ color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={phoneOn} disabled={!search?.phoneAvailable} onChange={(e) => setIncludePhone(e.target.checked)} />}
            label={t('hunting.search.phone.switch', { points: fmtPoints(pricing.phone) })}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {!search?.phoneAvailable
              ? t('hunting.search.phone.unavailable')
              : phoneOn
                ? t('hunting.search.phone.help', { count: willFetch.filter((p) => p.hasPhone).length, total: willFetch.length })
                : t('hunting.search.phone.offHelp')}
          </Typography>
        </Box>
        {renderAppliedFilters()}
        {people.length > 0 && (
          <FormControlLabel
            sx={{ mb: 1 }}
            control={<Checkbox size="small" checked={onlyWithPhone} onChange={(e) => toggleOnlyWithPhone(e.target.checked)} />}
            label={(
              <Typography variant="body2">
                {t('hunting.search.results.onlyWithPhone')}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {t('hunting.search.results.onlyWithPhoneCount', { count: withPhoneCount, total: people.length })}
                </Typography>
              </Typography>
            )}
          />
        )}
        {people.length === 0 ? (
          <Alert severity="info">{t('hunting.search.results.empty')}</Alert>
        ) : visible.length === 0 ? (
          <Alert severity="info">{t('hunting.search.results.noneWithPhone')}</Alert>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.size === ids.length && ids.length > 0}
                      indeterminate={selected.size > 0 && selected.size < ids.length}
                      onChange={() => toggleAll(selected, setSelected, ids)}
                    />
                  </TableCell>
                  <TableCell>{t('hunting.search.columns.name')}</TableCell>
                  <TableCell>{t('hunting.search.columns.title')}</TableCell>
                  <TableCell>{t('hunting.search.columns.company')}</TableCell>
                  <TableCell align="center">{t('hunting.search.columns.hasEmail')}</TableCell>
                  <TableCell align="center">{t('hunting.search.columns.hasPhone')}</TableCell>
                  <TableCell align="center">{t('hunting.search.columns.hasLocation')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((p) => (
                  <TableRow key={p.apolloId} hover selected={selected.has(p.apolloId)} onClick={() => toggle(selected, setSelected, p.apolloId)} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox"><Checkbox checked={selected.has(p.apolloId)} /></TableCell>
                    <TableCell>
                      {listName(p)}
                      {p.obtained && (
                        <Tooltip title={t('hunting.search.results.obtainedTip', { date: p.obtained.enrichedAt ? dayjs(p.obtained.enrichedAt).format('DD/MM/YYYY') : '—' })}>
                          <Chip size="small" label={t('hunting.search.results.obtained')} sx={{ ml: 1, backgroundColor: '#E0E7FF', color: '#3730A3' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{p.title || '—'}</TableCell>
                    <TableCell>{p.organizationName || '—'}</TableCell>
                    <TableCell align="center"><Flag value={p.hasEmail} title={t('hunting.search.columns.hasEmail')} /></TableCell>
                    <TableCell align="center">
                      <Flag value={p.hasPhone} title={t(p.hasPhone ? 'hunting.search.phone.flagYes' : 'hunting.search.phone.flagMaybe')} />
                    </TableCell>
                    <TableCell align="center"><Flag value={p.hasLocation} title={t('hunting.search.columns.hasLocation')} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  const renderEnriched = () => {
    const people = enrich?.people || [];
    const ids = people.filter((p) => p.email).map((p) => p.apolloId);
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {t('hunting.search.enriched.count', { matched: enrich.matched, requested: enrich.requested })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('hunting.search.enriched.pointsCharged', { points: fmtPoints(enrich.pointsCharged) })}
              {enrich.reused ? ` · ${t('hunting.search.enriched.reused', { count: enrich.reused })}` : ''}
              {enrich.phoneRequested ? ` · ${t('hunting.search.enriched.phoneRequested', { count: enrich.phoneRequested, points: fmtPoints((enrich.pricing || DEFAULT_PRICING).phone) })}` : ''}
              {enrich.notFound?.length ? ` · ${t('hunting.search.enriched.notFound', { count: enrich.notFound.length })}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(1)}>{t('hunting.search.actions.back')}</Button>
            <Button
              variant="contained" disabled={toInvite.size === 0} onClick={() => setActiveStep(3)}
              sx={{ backgroundColor: PURPLE, '&:hover': { backgroundColor: PURPLE_DARK } }}
            >
              {t('hunting.search.actions.goInvite', { count: toInvite.size })}
            </Button>
          </Box>
        </Box>
        {enrich.partialError && (
          <Alert severity="warning" sx={{ mb: 2 }}>{t('hunting.search.enriched.partial', { count: enrich.partialError.unprocessed || 0 })}</Alert>
        )}
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={toInvite.size === ids.length && ids.length > 0}
                    indeterminate={toInvite.size > 0 && toInvite.size < ids.length}
                    onChange={() => toggleAll(toInvite, setToInvite, ids)}
                  />
                </TableCell>
                <TableCell>{t('hunting.search.columns.name')}</TableCell>
                <TableCell>{t('hunting.search.columns.email')}</TableCell>
                <TableCell>{t('hunting.search.columns.phone')}</TableCell>
                <TableCell>{t('hunting.search.columns.linkedin')}</TableCell>
                <TableCell>{t('hunting.search.columns.location')}</TableCell>
                <TableCell>{t('hunting.search.columns.current')}</TableCell>
                <TableCell>{t('hunting.search.columns.experience')}</TableCell>
                <TableCell>{t('hunting.search.columns.inAmanda')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {people.map((p) => (
                <TableRow key={p.apolloId} hover selected={toInvite.has(p.apolloId)}>
                  <TableCell padding="checkbox">
                    <Checkbox checked={toInvite.has(p.apolloId)} disabled={!p.email} onChange={() => toggle(toInvite, setToInvite, p.apolloId)} />
                  </TableCell>
                  <TableCell>
                    {p.name || '—'}
                    {p.reused && (
                      <Tooltip title={t('hunting.search.enriched.reusedTip')}>
                        <Chip size="small" label={t('hunting.search.results.obtained')} sx={{ ml: 1, backgroundColor: '#E0E7FF', color: '#3730A3' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{p.email || '—'}</Typography>
                    <EmailStatusChip status={p.emailStatus} reliable={p.emailReliable} />
                    {(p.personalEmails || []).length > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('hunting.search.enriched.personalEmails', { list: p.personalEmails.join(', ') })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><PhoneCell phone={p.phone} late={phoneLate} /></TableCell>
                  <TableCell>
                    {p.linkedinUrl ? (
                      <Link href={p.linkedinUrl} target="_blank" rel="noopener" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        LinkedIn <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{p.location || '—'}</TableCell>
                  <TableCell>{[p.title, p.organizationName].filter(Boolean).join(' · ') || '—'}</TableCell>
                  <TableCell>
                    {(p.experience || []).slice(0, 2).map((e, i) => (
                      <Typography variant="caption" display="block" key={i}>
                        {[e.title, e.organizationName].filter(Boolean).join(' · ')}{e.startDate ? ` (${e.startDate}${e.current ? ` – ${t('hunting.search.enriched.current')}` : e.endDate ? ` – ${e.endDate}` : ''})` : ''}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell>
                    {p.existing ? (
                      <Tooltip title={(p.existing.positions || []).map((x) => x.positionName).filter(Boolean).join(', ') || t('hunting.search.enriched.registeredNoProcess')}>
                        <Chip size="small" label={t('hunting.search.enriched.registered', { count: p.existing.positions?.length || 0 })} sx={{ backgroundColor: '#E0E7FF', color: '#3730A3' }} />
                      </Tooltip>
                    ) : <Typography variant="caption" color="text.secondary">{t('hunting.search.enriched.new')}</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderInvite = () => {
    const hasAnyPosition = (positions.ats || []).length + (positions.evaluations || []).length > 0;
    const canSubmit = invitees.length > 0 && (!associate || Boolean(positionId));
    const submitLabel = associate && selectedIsAts && sendEmail && !emailBlock
      ? t('hunting.search.actions.invite', { count: invitees.length })
      : associate
        ? t('hunting.search.actions.associate', { count: invitees.length })
        : t('hunting.search.actions.save', { count: invitees.length });
    return (
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: 2, maxWidth: 760 }}>
        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
          {t('hunting.search.invite.title', { count: invitees.length })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('hunting.search.invite.saveNote')}
        </Typography>

        <FormControlLabel
          control={<Switch checked={associate} onChange={(e) => { setAssociate(e.target.checked); if (!e.target.checked) { setPositionId(''); setSendEmail(false); } }} />}
          label={t('hunting.search.invite.associateSwitch')}
          sx={{ mb: 1, display: 'flex' }}
        />
        {associate && (
          <Box sx={{ pl: 1, mb: 2 }}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>{t('hunting.search.invite.position')}</InputLabel>
              <Select value={positionId} label={t('hunting.search.invite.position')} onChange={(e) => { setPositionId(e.target.value); setSendEmail(false); }}>
                {(positions.ats || []).length > 0 && <ListSubheader>{t('hunting.search.invite.groupAts')}</ListSubheader>}
                {(positions.ats || []).map((p) => <MenuItem key={p.uid} value={p.uid}>{p.name}</MenuItem>)}
                {(positions.evaluations || []).length > 0 && <ListSubheader>{t('hunting.search.invite.groupEvaluations')}</ListSubheader>}
                {(positions.evaluations || []).map((p) => <MenuItem key={p.uid} value={p.uid}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
            {!hasAnyPosition && <Alert severity="warning" sx={{ mb: 1 }}>{t('hunting.search.invite.noPositions')}</Alert>}
            {selectedPosition && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {selectedIsAts ? t('hunting.search.invite.atsNote') : t('hunting.search.invite.evaluationsNote')}
              </Typography>
            )}
            {selectedIsAts && (
              <>
                <FormControlLabel
                  control={<Switch checked={sendEmail && !emailBlock} onChange={(e) => setSendEmail(e.target.checked)} />}
                  label={t('hunting.search.invite.emailSwitch')}
                  disabled={Boolean(emailBlock)}
                />
                {emailBlock && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t(`ats.portalInvitation.blocked.${emailBlock}`, { status: getStatusLabel(selectedPosition.status, t) })}
                  </Typography>
                )}
                {sendEmail && !emailBlock && <Alert severity="warning" sx={{ mt: 1 }}>{t('hunting.search.invite.emailNote')}</Alert>}
              </>
            )}
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          {invitees.map((p) => (
            <Chip key={p.apolloId} label={`${p.name} · ${p.email}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(2)}>{t('hunting.search.actions.back')}</Button>
          <Button
            variant="contained" disabled={!canSubmit || loading} onClick={runInvite}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{ backgroundColor: PURPLE, '&:hover': { backgroundColor: PURPLE_DARK } }}
          >
            {submitLabel}
          </Button>
        </Box>
      </Paper>
    );
  };

  const renderSummary = () => {
    const sm = summary?.summary || {};
    const results = summary?.results || [];
    const statusChip = (r) => {
      if (r.status === 'error') return <Chip size="small" label={`${t('hunting.search.summary.error')}: ${r.reason || ''}`} sx={{ backgroundColor: '#FEE2E2', color: '#991B1B' }} />;
      const label = r.status === 'sent' ? t('hunting.search.summary.sent') : r.status === 'added' ? t('hunting.search.summary.added') : t('hunting.search.summary.saved');
      return <Chip size="small" label={label} sx={{ backgroundColor: '#D1FAE5', color: '#065F46' }} />;
    };
    return (
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: 2, maxWidth: 760 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{t('hunting.search.summary.title')}</Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {t('hunting.search.summary.saved_line', { count: sm.saved || 0 })}
          {sm.positionName ? ` · ${t('hunting.search.summary.associated_line', { count: sm.associated || 0, position: sm.positionName })}` : ''}
          {sm.channel === 'email' ? ` · ${t('hunting.search.summary.emails_line', { count: sm.emailsSent || 0 })}` : ''}
          {sm.errors ? ` · ${t('hunting.search.summary.errors_line', { count: sm.errors })}` : ''}
        </Typography>
        {sm.alreadyInProcess ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t('hunting.search.summary.already', { count: sm.alreadyInProcess })}
          </Typography>
        ) : null}
        {sm.phonesPending ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t('hunting.search.summary.phonesPending', { count: sm.phonesPending })}
          </Typography>
        ) : null}
        {/* La vacante dejó de recibir postulaciones en el portal: no salió el correo */}
        {sm.emailBlocked ? (
          <Alert severity="warning" sx={{ mb: 1 }}>{t('ats.portalInvitation.notSent')}</Alert>
        ) : null}
        <Table size="small" sx={{ mb: 2, mt: 1 }}>
          <TableBody>
            {results.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.email}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {r.phone
                    ? formatValidationPhone(r.phone)
                    : r.phoneStatus === 'pending'
                      ? <Typography variant="caption" color="text.secondary">{t('hunting.search.enriched.phonePending')}</Typography>
                      : null}
                </TableCell>
                <TableCell>{statusChip(r)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={reset} sx={{ borderColor: PURPLE, color: PURPLE }}>{t('hunting.search.actions.newSearch')}</Button>
          {sm.positionId && sm.positionKind === 'ats' && (
            <Button
              variant="contained" onClick={() => { useStore.getState().setAppMode('ats'); navigate(`/ats/positions/${sm.positionId}`); }}
              sx={{ backgroundColor: PURPLE, '&:hover': { backgroundColor: PURPLE_DARK } }}
            >
              {t('hunting.search.actions.viewProcess')}
            </Button>
          )}
          {sm.positionKind !== 'ats' && (
            <Button
              variant="contained" onClick={() => { useStore.getState().setAppMode('legacy'); navigate('/applicants'); }}
              sx={{ backgroundColor: PURPLE, '&:hover': { backgroundColor: PURPLE_DARK } }}
            >
              {t('hunting.search.actions.viewApplicants')}
            </Button>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>{t('hunting.search.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t('hunting.search.subtitle')}</Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 3, maxWidth: 900 }}>
        {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {activeStep === 0 && renderSearchForm()}
      {activeStep === 1 && search && renderPartialResults()}
      {activeStep === 2 && enrich && renderEnriched()}
      {activeStep === 3 && renderInvite()}
      {activeStep === 4 && summary && renderSummary()}
    </Box>
  );
};

export default HuntingSearchLayout;
